import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  catchError,
  Observable,
  of,
  share,
  Subject,
  switchMap,
  take,
} from 'rxjs';
import { API_URL } from 'src/app/tokens/api-url.token';
import { HttpService } from 'src/app/services/http.service';
import { Summary } from 'src/app/shared/interfaces/base-response-interface';
import { DistinctValuesResponse } from 'src/app/shared/interfaces/distinct-values-response.interface';
import { SummaryByOriginatorResponseInterface } from 'src/app/shared/interfaces/summary-by-originator-response.interface';
import { TradeDetailsRequestInterfacae } from 'src/app/shared/interfaces/trade-details.interface';
import { TradeTransactionsQueryParams } from 'src/app/shared/interfaces/trade-transactions-query-params';
import { TradeTransactionsResponseInterface } from 'src/app/shared/interfaces/trade-transactions-response.interface';
import { ITradeDocumentPayload } from '../interfaces/i-trade-document.payload';
import { ITradeDocumentDto } from '../interfaces/i-trade-document.dto';

@Injectable({ providedIn: 'root' })
export class TradeHttpService extends HttpService {
  private requestTrigger$ = new Subject<
    Partial<TradeTransactionsQueryParams>
  >();
  private responseSubject$ = new Subject<TradeTransactionsResponseInterface>();
  private summaryTrigger$ = new Subject<
    Partial<TradeTransactionsQueryParams>
  >();
  private summarySubject$ = new Subject<Summary>();
  constructor(
    @Inject(API_URL) originatorBaseUrl: string,
    override http: HttpClient,
  ) {
    super(originatorBaseUrl, http);
    this.requestTrigger$ = new Subject();
    this.summaryTrigger$ = new Subject();
    this.setupRequestHandler(
      'etr',
      this.requestTrigger$,
      this.responseSubject$,
    );
    this.setupRequestHandler(
      'etr/summary',
      this.summaryTrigger$,
      this.summarySubject$,
    );
  }

  getTradeTransactionsData(
    params: Partial<TradeTransactionsQueryParams>,
  ): Observable<TradeTransactionsResponseInterface> {
    this.requestTrigger$.next(params);
    return this.responseSubject$.pipe(take(1));
  }

  getEtrSummary(
    params: Partial<TradeTransactionsQueryParams>,
  ): Observable<Summary> {
    this.summaryTrigger$.next(params);
    return this.summarySubject$.pipe(take(1));
  }

  getTradeTransactionsDataMultipleCall(
    params: Partial<TradeTransactionsQueryParams>,
  ): Observable<TradeTransactionsResponseInterface> {
    return this.http.get<TradeTransactionsResponseInterface>(
      `${this.originatorBaseUrl}etr`,
      {
        params: this.queryCleaner(params),
      },
    );
  }

  getEtrByDebtor(
    params: Partial<TradeTransactionsQueryParams>,
  ): Observable<TradeTransactionsResponseInterface> {
    return this.http.get<TradeTransactionsResponseInterface>(
      `${this.originatorBaseUrl}etr/by-debtor`,
      {
        params: this.queryCleaner(params),
      },
    );
  }

  getCredebtorOverviewList(
    params: Partial<TradeTransactionsQueryParams>,
  ): Observable<SummaryByOriginatorResponseInterface[]> {
    return this.http.get<SummaryByOriginatorResponseInterface[]>(
      `${this.originatorBaseUrl}credebtors/overview`,
      {
        params: this.queryCleaner(params),
      },
    );
  }

  getCreditorsList(): Observable<any> {
    return this.http.get<any>(`${this.originatorBaseUrl}credebtors/creditors`);
  }

  getDebtorsList(): Observable<any> {
    return this.http.get<any>(`${this.originatorBaseUrl}credebtors/debtors`);
  }

  getEtrSummaryMultipleCall(
    params: Partial<TradeTransactionsQueryParams>,
  ): Observable<Summary> {
    return this.http.get<Summary>(`${this.originatorBaseUrl}etr/summary`, {
      params: this.queryCleaner(params),
    });
  }

  getCurrencyFilterData(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}currencies`);
  }

  getEtrFilterDistinctValues(): Observable<DistinctValuesResponse> {
    return this.http.get<DistinctValuesResponse>(
      `${this.originatorBaseUrl}etr/distinct-filter-values`,
    );
  }

  postTrade(data: TradeDetailsRequestInterfacae): Observable<any> {
    return this.http.post<any>(`${this.originatorBaseUrl}acquisition`, data);
  }

  updateTrade(payload: {
    data: TradeDetailsRequestInterfacae;
    acquisitionId: string;
  }): Observable<any> {
    return this.http.patch<any>(
      `${this.originatorBaseUrl}acquisition/${payload.acquisitionId}`,
      payload.data,
    );
  }

  addTransaction(dto: any): Observable<any> {
    const file = dto.file;
    delete dto.file;
    const formData = new FormData();
    formData.append('file', file, file.name);
    Object.entries(dto).forEach(([key, value]) => {
      formData.append(key, value as string);
    });

    return this.http.post<any>(
      `${this.originatorBaseUrl}etr/transaction`,
      formData,
    );
  }

  getRejectedTrades(
    params: Partial<TradeTransactionsQueryParams>,
  ): Observable<TradeTransactionsResponseInterface> {
    return this.http.get<TradeTransactionsResponseInterface>(
      `${this.originatorBaseUrl}acquisition/rejected`,
      {
        params: this.queryCleaner(params),
      },
    );
  }

  uploadFile(file: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const baseUrl = this.originatorBaseUrl + 'document/upload-file';
    return this.http.post(baseUrl, formData);
  }

  uploadTradeDocument(
    file: File,
    documentPayload: ITradeDocumentPayload,
  ): Observable<ITradeDocumentDto> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    Object.entries(documentPayload).forEach(([key, value]) => {
      formData.append(key, value);
    });
    return this.http.post<ITradeDocumentDto>(
      this.originatorBaseUrl + 'document/upload-file',
      formData,
    );
  }

  deleteUploadedFile(id: string): Observable<any> {
    const baseUrl = this.baseUrl + `document/${id}`;
    return this.http.delete(baseUrl);
  }

  getDocumentsProperty(id: string): Observable<any> {
    return this.http.get<any>(
      `${this.originatorBaseUrl}credebtors/by-entity-role-id/${id}`,
    );
  }

  acquisitionImport(file: any): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const baseUrl = this.originatorBaseUrl + 'acquisition/import';
    return this.http.post(baseUrl, formData);
  }

  private setupRequestHandler(
    endpoint: string,
    trigger$: Subject<any>,
    subject: Subject<any>,
  ) {
    trigger$
      .pipe(
        switchMap((params) => {
          if (params === null) return of([]);
          return this.http
            .get(`${this.originatorBaseUrl}${endpoint}`, {
              params: this.queryCleaner(params),
            })
            .pipe(
              catchError((error) => {
                subject.error(error);
                return of(null);
              }),
            );
        }),
        share(),
      )
      .subscribe(subject);
  }
}
